import React, { useState, useRef, lazy, Component } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ArrowRightIcon, StarIcon, AlertCircleIcon, LinkIcon, GitMergeIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
// Helper: series status color — matches UnifiedBookCard's getStatusColor exactly
const getSeriesStatusColor = (status: string): string => {
  const s = status?.toLowerCase() || '';
  if (s === 'complete' || s === 'backlist') return 'bg-green-600';
  if (s === 'ongoing') return 'bg-yellow-500';
  if (s.includes('hiatus') || s.includes('cancel') || s.includes('discontinu') || s.includes('stopped') || s.includes('removed')) return 'bg-red-600';
  return 'bg-blue-600';
};
// Helper: series status label
const getSeriesStatusLabel = (status: string): string => {
  const s = status?.toLowerCase() || '';
  if (s === 'complete' || s === 'backlist') return 'Complete';
  if (s === 'ongoing') return 'Ongoing';
  if (s.includes('hiatus')) return 'Hiatus';
  if (s.includes('cancel') || s.includes('discontinu')) return 'Discontinued';
  return status ? status.charAt(0).toUpperCase() + status.slice(1) : '';
};
interface SeriesCarouselProps {
  series: any[];
  darkMode?: boolean;
}
// Helper for genre colors
const getGenreColor = (genre: string): string => {
  const genreLower = genre.toLowerCase();
  if (genreLower.includes('romance')) return 'bg-pink-600';
  if (genreLower.includes('fantasy')) return 'bg-indigo-600';
  if (genreLower.includes('sci-fi') || genreLower.includes('science')) return 'bg-blue-600';
  if (genreLower.includes('mystery')) return 'bg-indigo-600';
  if (genreLower.includes('thriller')) return 'bg-red-600';
  if (genreLower.includes('horror')) return 'bg-gray-800';
  if (genreLower.includes('historical')) return 'bg-amber-600';
  if (genreLower.includes('young adult') || genreLower.includes('ya')) return 'bg-cyan-600';
  if (genreLower.includes('contemporary')) return 'bg-emerald-600';
  return 'bg-violet-600'; // Default color
};
// Helper: book count badge color matches series status
const getBookCountBadgeColor = (status: string, hasPreorder?: boolean): string => {
  const s = status?.toLowerCase() || '';
  if (hasPreorder && s === 'ongoing') return 'bg-amber-500/80';
  if (s === 'complete' || s === 'backlist' || s === 'spin_off_active') return 'bg-green-600/80';
  if (s === 'ongoing') return 'bg-blue-500/80';
  if (s.includes('hiatus')) return 'bg-amber-500/80';
  if (s.includes('cancel') || s.includes('discontinu')) return 'bg-red-600/80';
  return 'bg-indigo-500/80';
};
const SeriesCarousel: React.FC<SeriesCarouselProps> = ({
  series,
  darkMode = false
}) => {
  const dk = useDarkClasses();
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: -300,
        behavior: 'smooth'
      });
    }
  };
  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: 300,
        behavior: 'smooth'
      });
    }
  };
  if (!series || series.length === 0) return null;
  return <div className="relative group" data-id="element-1007" data-mp="3cts7o3cl6ix4">
      <div ref={scrollRef} className="flex overflow-x-auto gap-3 sm:gap-4 pb-4 snap-x px-2 scrollbar-hide" style={{
      scrollbarWidth: 'none',
      msOverflowStyle: 'none'
    }} data-id="element-1008" data-mp="3hsd9g77hlano">
        {series.map(seriesItem => <SeriesCard key={seriesItem.id} series={seriesItem} darkMode={darkMode} data-id="element-1009" data-mp="2p3xypwve1ei5" />)}
      </div>

      {/* Navigation arrows */}
      <button onClick={scrollLeft} className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 rounded-full p-2 shadow-md opacity-0 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200 z-30 ${dk.isDark ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700'}`} aria-label="Scroll left" data-id="element-1010" data-mp="2y55m3jwd8uxg">
        <ChevronLeftIcon className="h-6 w-6" data-id="element-1011" data-mp="6y4upv2yivw3" />
      </button>
      <button onClick={scrollRight} className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 rounded-full p-2 shadow-md opacity-0 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200 z-30 ${dk.isDark ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700'}`} aria-label="Scroll right" data-id="element-1012" data-mp="6zpovjld0ctf">
        <ChevronRightIcon className="h-6 w-6" data-id="element-1013" data-mp="pxiu4sjggytz" />
      </button>
    </div>;
};
// Individual Series Card Component
const SeriesCard = ({
  series,
  darkMode
}: {
  series: any;
  darkMode: boolean;
}) => {
  const [imgError, setImgError] = useState(false);
  const rt = useRoleTheme();
  const coverImage = series.coverImage || series.books && series.books[0]?.coverImage || '';
  const showEndingType = (series.status === 'complete' || series.status === 'backlist') && series.endingType;
  const showPreOrder = series.status === 'ongoing' && series.preorderBook && !showEndingType;
  const publishedCount = series.books?.length || 0;
  const totalCount = series.totalBooks || publishedCount;
  const bookCountLabel = totalCount > 0 ? `${publishedCount}/${totalCount}` : `${publishedCount}`;
  const bookCountColor = getBookCountBadgeColor(series.status, !!series.preorderBook);
  const authorSlug = series.author?.toLowerCase().replace(/\s+/g, '-') || '';
  return <div className="flex-shrink-0 w-36 sm:w-40 md:w-48 snap-start group/card relative" data-id="element-1014" data-mp="1e9spzwu989q9">
      <Link to={`/series/${series.id}`} className="block relative aspect-[2/3] rounded-lg overflow-hidden shadow-md transition-transform duration-200 group-hover/card:scale-105" data-id="element-1015" data-mp="10i4dhsg3fj8r">
        {imgError ? <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-indigo-100 via-indigo-200 to-indigo-300" data-id="element-1016" data-mp="qkpxtv1zuyhi">
            <span className="text-indigo-500 text-xs font-medium text-center px-2 line-clamp-3" data-id="element-1017" data-mp="vkuizydukhe2">
              {series.name}
            </span>
          </div> : <img src={coverImage} alt={series.name} className="w-full h-full object-cover" loading="lazy" onError={() => setImgError(true)} data-id="element-1018" data-mp="2sfnr84vl9pxt" />}

        {/* ALWAYS VISIBLE ON COVER */}

        {/* Top Left: Genre Tag */}
        {series.genres && series.genres[0] && <span className={`absolute top-2 left-2 text-white text-xs font-medium ${getGenreColor(series.genres[0])} px-2 py-0.5 ${rt.badgeRadius} shadow-sm z-10`} data-id="element-1019" data-mp="3c5hblxvz52s0">
            {series.genres[0]}
          </span>}

        {/* Top Right: Status Badge — inline span matching UnifiedBookCard style */}
        {series.status && <span className={`absolute top-2 right-2 text-white text-xs ${getSeriesStatusColor(series.status)} px-2 py-0.5 ${rt.badgeRadius} z-10`} data-id="element-1020" data-mp="1vs9as7pxpszs">
            {getSeriesStatusLabel(series.status)}
          </span>}

        {/* Bottom Left: Content Warning Icon */}
        {series.contentWarnings && series.contentWarnings.length > 0 && <div className="absolute bottom-2 left-2 z-10" title={`Content Warnings: ${series.contentWarnings.join(', ')}`} data-id="element-1021" data-mp="1ql2sjh9zztuw">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-500 text-white shadow-md" data-id="element-1022" data-mp="1zefv7axv69u6">
              <AlertCircleIcon className="h-3.5 w-3.5" data-id="element-1023" data-mp="24cpqdljl9afs" />
            </span>
          </div>}

        {/* HOVER OVERLAY */}
        <div className="absolute inset-0 bg-black/85 backdrop-blur-sm opacity-0 group-hover/card:opacity-100 flex flex-col p-3 transition-opacity duration-200 text-white z-20" data-id="element-1024" data-mp="29d5268o829vn">
          <div className="min-w-0" data-id="element-1025" data-mp="ezm9bc54zfgo">
            {/* Series name + book count badge */}
            <div className="flex items-start justify-between gap-1 mb-0.5" data-id="element-1026" data-mp="jwvxkogshl2x">
              <Link to={`/series/${series.id}`} className="font-bold text-white text-sm line-clamp-1 hover:text-indigo-300 transition-colors flex-1 min-w-0" onClick={e => e.stopPropagation()} data-id="element-1027" data-mp="2luy10caj62ht">
                {series.name}
              </Link>
              <span className={`flex-shrink-0 text-[10px] font-bold text-white px-1.5 py-0.5 rounded ${bookCountColor}`} data-id="element-1028" data-mp="2qlee1vafvm3n">
                {bookCountLabel}
              </span>
            </div>

            {/* Author */}
            <p className="text-white/80 text-xs line-clamp-1 mb-2" data-id="element-1029" data-mp="1xz3d2jix7j6b">
              by{' '}
              <Link to={`/author/${authorSlug}`} className="hover:text-indigo-300 hover:underline transition-colors" onClick={e => e.stopPropagation()} data-id="element-1030" data-mp="2byl7pfiwdqnl">
                {series.author}
              </Link>
            </p>

            {/* Connection type pill */}
            {series.connectionType && <div className="mb-2" data-id="element-1031" data-mp="3qtuujbwgtipk">
                <span className={`inline-flex items-center ${rt.badgeRadius} border px-1.5 py-0.5 text-[10px] font-medium ${series.connectionType === 'connected' ? 'bg-teal-900/40 text-teal-200 border-teal-800' : 'bg-slate-800/60 text-slate-300 border-slate-700'}`} title={series.connectionType === 'connected' ? 'One continuous story (read in order)' : 'Same world, separate stories (can read independently)'} data-id="element-1032" data-mp="3qst9qfpndhun">
                  {series.connectionType === 'connected' ? <LinkIcon className="h-2.5 w-2.5 mr-1" data-id="element-1033" data-mp="3nkg3nopjzgd" /> : <GitMergeIcon className="h-2.5 w-2.5 mr-1" data-id="element-1034" data-mp="8moqghndc38a" />}
                  {series.connectionType === 'connected' ? 'Connected' : 'Interconnected'}
                </span>
              </div>}

            {/* Tags row */}
            <div className="flex flex-wrap gap-1 mb-2" data-id="element-1035" data-mp="2fckvqnhm1swz">
              {showEndingType && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-600/80 text-white" data-id="element-1036" data-mp="2jclo7c1jo4n1">
                  {series.endingType}
                </span>}
              {showPreOrder && <Link to={`/book/${series.preorderBook.id}`} className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/80 text-white hover:bg-amber-500 transition-colors" onClick={e => e.stopPropagation()} data-id="element-1037" data-mp="2aejg6cje8bq9">
                  Pre-Order Bk {series.preorderBook.number}
                </Link>}
              {series.spinOffName && series.spinOffId && <Link to={`/series/${series.spinOffId}`} className="px-1.5 py-0.5 rounded text-[10px] font-medium text-fuchsia-300 bg-fuchsia-900/40 border border-fuchsia-800 hover:text-fuchsia-200 transition-colors flex items-center" onClick={e => e.stopPropagation()} data-id="element-1038" data-mp="1cpzjvxrj4unn">
                  {series.spinOffName}{' '}
                  <ArrowRightIcon className="h-2.5 w-2.5 ml-0.5" data-id="element-1039" data-mp="180p2s12rhgc7" />
                </Link>}
              {series.contentWarnings && series.contentWarnings.length > 0 && <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500/30 text-amber-200" title={`Content Warnings: ${series.contentWarnings.join(', ')}`} data-id="element-1040" data-mp="1h0ll7kyk5iyt">
                  <AlertCircleIcon className="h-3 w-3" data-id="element-1041" data-mp="1qqeycqecm656" />
                </span>}
            </div>
          </div>

          {/* Bottom Row: Rating & CTA */}
          <div className="mt-auto flex items-center justify-between pt-2 border-t border-white/10" data-id="element-1042" data-mp="20ob2gy8vc0ts">
            <div className="flex items-center text-amber-400" data-id="element-1043" data-mp="1vr2lh0zgi2u4">
              <StarIcon className="h-3.5 w-3.5 fill-current" data-id="element-1044" data-mp="22t7udpp904mv" />
              <span className="ml-1 text-xs font-bold text-white" data-id="element-1045" data-mp="22uskn34j51ex">
                {series.rating}
              </span>
              <span className="ml-1 text-[10px] text-white/60" data-id="element-1046" data-mp="27py7c45rak42">
                ({series.reviews})
              </span>
            </div>
            <span className="text-[10px] font-medium text-white/70 group-hover/card:text-white transition-colors flex items-center" data-id="element-1047" data-mp="2hilzumppe1wz">
              View Series <ArrowRightIcon className="h-3 w-3 ml-0.5" data-id="element-1048" data-mp="2mgxdz9n16yl0" />
            </span>
          </div>
        </div>
      </Link>
    </div>;
};
export default SeriesCarousel;